import {
  EEventContextAction,
  EEventType,
  type IProductList,
} from '@mahawi/eshop-common/src/types';
import { type Dispatch } from '@reduxjs/toolkit';
import { type IProductState } from 'customer/react/reducers/product/types';
import { memo, type ReactElement, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { eventSend } from '../../reducers/event/reducer';
import Grid from '../products/grid';

export function dispatchRelatedProductClick(
  product: IProductState['product'],
  targetUuid: string,
  position: number,
  dispatch: Dispatch,
): void {
  if (!product) {
    return;
  }

  dispatch(
    eventSend({
      type: EEventType.RELATED_PRODUCT_CLICK,
      context: {
        action: EEventContextAction.CLICK,
        relatedProduct: {
          sourceUuid: product.uuid,
          targetUuid,
          position,
          algorithmVersion: 'v2',
        },
      },
    }),
  );
}

function RelatedComponent({
  Product,
  dispatch,
}: {
  Product: IProductState;
  dispatch: Dispatch;
}): ReactElement | null {
  const { t } = useTranslation();

  const relatedProducts: IProductList[] = useMemo(
    (): IProductList[] => Product.product?.relatedProducts ?? [],
    [Product.product?.relatedProducts],
  );

  const handleProductClick = useCallback(
    (targetUuid: string, position: number): void => {
      dispatchRelatedProductClick(
        Product.product,
        targetUuid,
        position,
        dispatch,
      );
    },
    [Product.product, dispatch],
  );

  if (!Product.product || relatedProducts.length === 0) {
    return null;
  }

  return (
    <div className="product related-products">
      <h2>{t('layout.relatedProducts')}</h2>
      <div className="related-products--carousel">
        <Grid
          products={relatedProducts}
          headerLevel={3}
          onProductClick={handleProductClick}
        />
      </div>
    </div>
  );
}

const Related = memo(RelatedComponent);

const mapStateToProps = ({ Product }: { Product: IProductState }) => ({
  Product,
});

export default connect(mapStateToProps)(Related);
